/**
 * Date windows for the Focus bar. Every date is a UTC calendar day
 * ("YYYY-MM-DD"); the product reports in UTC (DESIGN.md §6).
 *
 * "Last N days" windows are ANCHORED ON DATA: they end on the latest day
 * the scope has data for (Focus rule: each app is anchored on its own latest
 * data day), never on "today", so a provider that is a day behind does not
 * read as a collapse in delivery. Calendar shortcuts (this week, last month…)
 * are calendar facts and use today, as apps/web does.
 */
import type { Period } from './search';
import { formatUtcDay, formatUtcRange } from '@/lib/format';

export type Day = string;
export type WindowDays = 7 | 14 | 28;

/** What the page knows about the data behind the current scope. */
export interface DataBounds {
  /** First and last day with data for the scope (filter-values `date_range`). */
  first: Day | null;
  last: Day | null;
}

const toDate = (day: Day): Date => new Date(`${day}T00:00:00Z`);
const toDay = (date: Date): Day => date.toISOString().slice(0, 10);

export const todayUtc = (now: Date = new Date()): Day => toDay(now);

export const addDays = (day: Day, days: number): Day => {
  const date = toDate(day);
  date.setUTCDate(date.getUTCDate() + days);
  return toDay(date);
};

const mondayOf = (day: Day): Day => {
  const weekday = toDate(day).getUTCDay(); // 0 = Sunday
  return addDays(day, weekday === 0 ? -6 : 1 - weekday);
};

const firstOfMonth = (day: Day, monthOffset = 0): Day => {
  const date = toDate(day);
  return toDay(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + monthOffset, 1)));
};

const lastOfMonth = (day: Day, monthOffset = 0): Day => addDays(firstOfMonth(day, monthOffset + 1), -1);

export const PERIOD_LABEL: Record<Period, string> = {
  last_7: 'Last 7 days',
  last_14: 'Last 14 days',
  last_28: 'Last 28 days',
  this_week: 'This week',
  last_week: 'Last week',
  this_month: 'This month',
  last_month: 'Last month',
  month_to_date: 'Month to date',
  lifetime: 'Lifetime',
};

export const WINDOW_OF_PERIOD: Partial<Record<Period, WindowDays>> = { last_7: 7, last_14: 14, last_28: 28 };

export const periodOfWindow = (days: WindowDays): Period => (days === 7 ? 'last_7' : days === 28 ? 'last_28' : 'last_14');

/** The trailing `days`-day window ending on `anchor`, clamped to the first data day. */
export const trailingWindow = (days: number, anchor: Day, first: Day | null = null): { from: Day; to: Day } => {
  const from = addDays(anchor, -(days - 1));
  return { from: first && from < first ? first : from, to: anchor };
};

/**
 * The dates a shortcut stands for. Returns null only for Lifetime while the
 * data bounds are unknown (there is no honest stand-in for them).
 */
export function periodRange(period: Period, bounds: DataBounds, today: Day = todayUtc()): { from: Day; to: Day } | null {
  const days = WINDOW_OF_PERIOD[period];
  if (days) return trailingWindow(days, bounds.last ?? today, bounds.first);
  switch (period) {
    case 'this_week': {
      const monday = mondayOf(today);
      return { from: monday, to: addDays(monday, 6) };
    }
    case 'last_week': {
      const monday = addDays(mondayOf(today), -7);
      return { from: monday, to: addDays(monday, 6) };
    }
    case 'this_month':
      return { from: firstOfMonth(today), to: lastOfMonth(today) };
    case 'last_month':
      return { from: firstOfMonth(today, -1), to: lastOfMonth(today, -1) };
    case 'month_to_date':
      return { from: firstOfMonth(today), to: today };
    case 'lifetime':
      return bounds.first && bounds.last ? { from: bounds.first, to: bounds.last } : null;
    default:
      return null;
  }
}

/**
 * The shortcut the current dates still stand for, or null for a custom range.
 * While the bounds are unknown, a data-anchored shortcut is trusted as named.
 */
export function matchingPeriod(
  period: Period | undefined,
  dates: { from: Day; to: Day },
  bounds: DataBounds | null,
  today: Day = todayUtc(),
): Period | null {
  if (!period) return null;
  if (!bounds) return period;
  const range = periodRange(period, bounds, today);
  return range && range.from === dates.from && range.to === dates.to ? period : null;
}

/** "Sep 16 – Sep 29" in the current year; with years otherwise. */
export const formatDateChip = (from: Day, to: Day, today: Day = todayUtc()): string => {
  const year = today.slice(0, 4);
  return from.slice(0, 4) === year && to.slice(0, 4) === year ? `${formatUtcDay(from)} – ${formatUtcDay(to)}` : formatUtcRange(from, to);
};

/** True when the chosen window lies wholly outside the days that have data. */
export const outsideData = (dates: { from: Day; to: Day }, bounds: DataBounds | null): boolean =>
  Boolean(bounds?.first && bounds.last && (dates.to < bounds.first || dates.from > bounds.last));

/**
 * Whether the scope's data covers the chosen dates: `no_data` when nothing
 * matches the scope at all (the filters exclude everything), `outside` when
 * the dates lie wholly outside the days with data, `covered` otherwise
 * (including while the bounds are unknown).
 */
export type Coverage = 'no_data' | 'outside' | 'covered';

export const scopeCoverage = (dates: { from: Day; to: Day }, bounds: DataBounds | null): Coverage => {
  if (bounds && bounds.last === null) return 'no_data';
  return outsideData(dates, bounds) ? 'outside' : 'covered';
};
